import { Button, EmptyState, formatDate, PageHeader, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { History } from 'lucide-react';
import { LoadFailed } from '../LoadFailed.tsx';
import { LoadMore } from '../LoadMore.tsx';
import { usePageContext } from '../usePageContext.ts';
import {
  groupByDay,
  isFiltered,
  NO_HISTORY_FILTERS,
  useHistory,
  type HistoryEntry,
  type HistoryFilters,
} from './history.ts';
import { actorNameOf } from './history-format.ts';
import { HistoryFilterBar } from './HistoryFilterBar.tsx';
import { HistoryLine } from './HistoryLine.tsx';
import { useValueContext } from './useValueContext.ts';
import styles from './History.module.css';
import { BackCrumb } from '../BackCrumb.tsx';

const SKELETON_LINES = 4;

export type HistoryPageProps = {
  api: TypedClient;
  filters: HistoryFilters;
  onFiltersChange: (filters: HistoryFilters) => void;
  onOpenSubject: (entry: HistoryEntry) => void;
  onBack: () => void;
};

type ResultsProps = Omit<HistoryPageProps, 'onBack'> & {
  history: ReturnType<typeof useHistory>;
  onChange: (next: Partial<HistoryFilters>) => void;
};

const HistoryResults = ({
  api,
  history,
  filters,
  onChange,
  onFiltersChange,
  onOpenSubject,
}: ResultsProps) => {
  const { t, locale, describeError } = usePageContext();
  const context = useValueContext(api);
  const filtered = isFiltered(filters);

  if (history.query.isPending)
    return (
      <div className={styles['skeletons']}>
        {Array.from({ length: SKELETON_LINES }, (_, index) => (
          <Skeleton key={index} height="40px" label={t('history.loading')} />
        ))}
      </div>
    );
  if (history.query.isError)
    return (
      <LoadFailed
        description={describeError(history.query.error)}
        onRetry={() => {
          void history.query.refetch();
        }}
      />
    );
  if (history.items.length === 0)
    return (
      <EmptyState
        icon={<History size={24} />}
        title={filtered ? t('history.emptyFiltered') : t('history.empty')}
        description={filtered ? t('history.emptyFilteredHint') : t('history.emptyHint')}
        action={
          filtered ? (
            <Button
              variant="outline"
              onClick={() => {
                onFiltersChange(NO_HISTORY_FILTERS);
              }}
            >
              {t('history.clearFilters')}
            </Button>
          ) : undefined
        }
      />
    );
  return (
    <div className={styles['days']}>
      {groupByDay(history.items).map(({ day, entries }) => (
        <section key={day} className={styles['day']} aria-labelledby={`history-${day}`}>
          <h2 id={`history-${day}`}>{formatDate(day, locale, 'long')}</h2>
          <ol className={styles['lines']}>
            {entries.map((entry) => (
              <HistoryLine
                key={entry.id}
                entry={entry}
                context={context}
                onFilterActor={(actor) => {
                  onChange({ actor });
                }}
                onOpenSubject={entry.kind === null ? null : onOpenSubject}
              />
            ))}
          </ol>
        </section>
      ))}
      <LoadMore
        shownLabel={t('history.shown', { count: history.items.length, total: history.total })}
        hasMore={history.hasMore}
        loading={history.loadingMore}
        onLoadMore={history.loadMore}
      />
    </div>
  );
};

export const HistoryPage = ({ onBack, ...props }: HistoryPageProps) => {
  const { t } = usePageContext();
  const history = useHistory(props.api, props.filters);
  const onChange = (next: Partial<HistoryFilters>) => {
    props.onFiltersChange({ ...props.filters, ...next });
  };
  const actorEntry = history.items.find((entry) => entry.actor.userId === props.filters.actor);

  return (
    <>
      <PageHeader
        title={t('history.title')}
        context={t('history.context')}
        breadcrumbs={[{ label: t('nav.settings'), href: '/settings' }, { label: t('history.title') }]}
        renderLink={(crumb) => <BackCrumb label={crumb.label} onBack={onBack} />}
      />
      <HistoryFilterBar
        filters={props.filters}
        actorName={actorEntry === undefined ? null : actorNameOf(actorEntry.actor, t)}
        onChange={onChange}
      />
      <HistoryResults {...props} history={history} onChange={onChange} />
    </>
  );
};
